/* eslint-disable no-magic-numbers */
import React from 'react';
import { Input } from '@/shared/Input';
import { Select } from '@/shared/Select';
import { LoadingIndicator } from '@/shared/LoadingIndicator';
import { ColorTilePicker } from '@/shared/ColorPicker/ColorTilePicker.component';
import { normalizeHexColor } from '@/utils/convert/normalizeHexColor';
import { useAppSelector } from '@/redux/hooks/useAppSelector';
import { lineTypesKeysSelector } from '@/redux/shapes/shapes.selectors';
import {
  MAX_LINE_WIDTH,
  MIN_LINE_WIDTH,
} from '@/components/FunctionalArea/Modal/LayerLineFactoryModal/LayerLineFactory.constants';
import { EditElementSection } from '@/components/FunctionalArea/Modal/EditElementModal/EditElementSection.component';
import { useReactionLines } from '@/components/FunctionalArea/Modal/EditReactionModal/utils/useReactionLines';

const fieldLabelClass = 'text-xs font-medium text-font-500';
const gridClass = 'grid grid-cols-3 gap-x-3 gap-y-4';

/**
 * Line styling for the reaction as a whole. The controls are seeded from the
 * reaction line, and any change is applied to every line of the reaction -
 * including the lines of its reactants, products, modifiers and operators.
 */
export const ReactionLinesForm: React.FC = () => {
  const {
    hasLines,
    isSending,
    width,
    setWidth,
    clampLineWidth,
    handleWidthBlur,
    color,
    lineType,
    saveAllLines,
  } = useReactionLines();

  const lineTypeKeys = useAppSelector(lineTypesKeysSelector);
  const lineTypeOptions = lineTypeKeys.map(key => ({ id: key, name: key }));

  if (!hasLines) {
    return null;
  }

  return (
    <EditElementSection title="Line" defaultExpanded={false}>
      {isSending && (
        <div className="c-edit-element-modal-loader">
          <LoadingIndicator width={44} height={44} />
        </div>
      )}
      <div className={gridClass}>
        <div className="flex flex-col gap-1">
          <span className={fieldLabelClass}>Line width</span>
          <Input
            type="number"
            name="line-width"
            id="line-width"
            data-testid="reaction-line-width"
            min={MIN_LINE_WIDTH}
            max={MAX_LINE_WIDTH}
            value={width}
            onChange={e => setWidth(clampLineWidth(Number(e.target.value)))}
            onBlur={handleWidthBlur}
            className="text-xs font-medium text-font-400"
          />
        </div>
        <div className="flex flex-col gap-1">
          <span className={fieldLabelClass}>Line type</span>
          <Select
            options={lineTypeOptions}
            selectedId={lineType || null}
            onChange={selectedId => saveAllLines({ lineType: String(selectedId) })}
          />
        </div>
        <div className="flex flex-col gap-1">
          <span className={fieldLabelClass}>Line color</span>
          <ColorTilePicker
            initialColor={color}
            colorChange={newColor => saveAllLines({ color: normalizeHexColor(newColor) })}
            placement="left"
          />
        </div>
      </div>
      <p className="text-xs font-normal text-font-400">
        Applies to the reaction line and the lines of all its reactants, products, modifiers and
        operators.
      </p>
    </EditElementSection>
  );
};
